<script lang="ts" setup>
import { ElNotification } from 'element-plus'
import type { UserInfo } from '~/models/userInfo'
import type { UserPreferences } from '~/services/apiClient'
import { UsersApi } from '~/services/apiClient'
import { useUserStore } from '~/stores/user'

const userStore = useUserStore()
const savedUserPreferences = ref('')
const userPreferences = ref(null as UserPreferences | null)

function markUserPreferencesAsSaved(userInfo: UserInfo) {
  userPreferences.value = userInfo.preferences
  savedUserPreferences.value = JSON.stringify(userPreferences.value)
}

onMounted(() => {
  userStore.loadInfoFromAPI().then(async (userInfo) => {
    markUserPreferencesAsSaved(userInfo)
  })
})

const userPreferencesModified = computed(() => {
  return JSON.stringify(userPreferences.value) !== savedUserPreferences.value
})

function saveUserPreferences() {
  const api = new UsersApi()
  if (!userPreferences.value) throw new Error('This should not happen')
  api
    .updateCurrentUserPreferencesUsersCurrentPreferencesPut(
      userPreferences.value
    )
    .then(() => {
      userStore.loadInfoFromAPI()
      ElNotification({
        title: 'Preferences saved',
        type: 'success',
      })
      userStore.loadInfoFromAPI().then(markUserPreferencesAsSaved)
    })
    .catch(() => {
      ElNotification({
        message: 'Error saving preferences',
        type: 'error',
      })
    })
}

const autoBookNotificationOption = [
  {
    minutes: 1,
    description: 'Every minute',
  },
  {
    minutes: 5,
    description: 'Every 5 minutes',
  },
  {
    minutes: 15,
    description: 'Every 15 minutes',
  },
  {
    minutes: 30,
    description: 'Every 30 minutes',
  },
  {
    minutes: 60,
    description: 'Every hour',
  },
  {
    minutes: 120,
    description: 'Every 2 hours',
  },
  {
    minutes: 180,
    description: 'Every 3 hours',
  },
  {
    minutes: 240,
    description: 'Every 4 hours',
  },
  {
    minutes: 360,
    description: 'Every 6 hours',
  },
]
</script>

<template>
  <div v-loading="userStore.loading" class="min-h-30">
    <div v-if="userPreferences">
      <!-- Section header with save button -->
      <div class="flex justify-between items-center mb-3">
        <h3 class="subsection-header !mt-0 !mb-0">Auto Book Email Notifications</h3>
        <el-button
          type="success"
          :disabled="!userPreferencesModified"
          @click="saveUserPreferences"
        >
          Save
        </el-button>
      </div>
      
      <el-form label-position="top">
        <el-form-item>
          <template #label>
            Auto Book notification frequency:
            <Help title="Auto Book Notification Frequency" class="help inline-block">
              <p>
                Auto Book email notifications for successful bookings are grouped
                into emails sent at this interval. Notifications for auto booked
                appointments within 12 hours are always sent immediately.
              </p>
              <p>
                It may take up to one "cycle" for modifications of this setting to
                take affect.
              </p>
            </Help>
          </template>
          <el-select
            v-model="userPreferences.auto_book_notification_interval_minutes"
          >
            <el-option
              v-for="option in autoBookNotificationOption"
              :key="option.minutes"
              :value="option.minutes"
              :label="option.description"
            >
              {{ option.description }}
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Styles are now in global stylesheet
</style>
